import React, { useMemo } from "react";
import { Trash2 } from "lucide-react";
import { fmt, expenseAmountINR } from "@/lib/finance";
import DriveLinkCell from "@/components/DriveLinkCell";
import { EditableText, EditableSelect, EditableDate, EditableMonth, EditableTextArea } from "@/components/EditableCell";

export const CURRENCIES = ["INR", "USD", "RMB"];
export const PAY_MODES = ["Bank Transfer", "UPI", "Card", "Cash", "Cheque"];
export const PAID_OPTIONS = [{ value: "paid", label: "Paid" }, { value: "unpaid", label: "Unpaid" }];

// Frozen/sticky columns (Date, Account, Party, Amount). CSS `position: sticky`
// needs a fixed pixel `left` per column, so widths here must match each <th>/<td>.
export const STICKY_W = { checkbox: 36, date: 100, account: 130, party: 170, amount: 120 };
export const COL_W = { status: 90, narration: 240, project: 130, department: 110, category: 130, subcategory: 130, paymode: 120, currency: 70, fx: 80, inrvalue: 110, month: 90, drivelink: 140, actions: 70 };

// Taxonomy helpers — pure functions over the categories tree so rows can
// memoize their own dependent option lists.
export function categoriesFor(tree, dept) {
  const scope = dept ? tree.filter((d) => d.department === dept) : tree;
  const set = new Set();
  scope.forEach((d) => (d.categories || []).forEach((c) => set.add(c.category)));
  return [...set].sort();
}
export function subsFor(tree, dept, cat) {
  if (!cat) return [];
  const scope = dept ? tree.filter((d) => d.department === dept) : tree;
  const set = new Set();
  scope.forEach((d) => (d.categories || []).forEach((c) => {
    if (c.category === cat) (c.sub_categories || []).forEach((s) => set.add(s.sub_category));
  }));
  return [...set].sort();
}

// One stable `(value) => updateCell(id, field, value)` per field per row, so
// memo'd cells don't see a new callback identity on every row render.
export function useFieldSetters(id, updateCell) {
  return useMemo(() => {
    const cache = {};
    return (field) => cache[field] || (cache[field] = (v) => updateCell(id, field, v));
  }, [id, updateCell]);
}

const ExpenseLedgerRow = React.memo(function ExpenseLedgerRow({
  row: r, dirty, selected, canBulk, RowCheckbox, updateCell,
  sourceOptions, vendorOptions, projectOptions, departments, tree,
  onRemove, onPreview, onAddVendor, onAddCategory,
}) {
  const set = useFieldSetters(r.id, updateCell);
  const categoryOptions = useMemo(() => categoriesFor(tree, r.department), [tree, r.department]);
  const subOptions = useMemo(() => subsFor(tree, r.department, r.category), [tree, r.department, r.category]);
  const stickyBg = dirty ? "bg-orange-50" : selected ? "bg-orange-100" : "bg-white";
  const stickyLeft = canBulk ? STICKY_W.checkbox : 0;
  const sourceLabel = sourceOptions.find((s) => s.value === r.source_id)?.label || "—";

  const setCascade = (dept) => { updateCell(r.id, "department", dept); updateCell(r.id, "category", ""); updateCell(r.id, "sub_category", ""); };
  const setCat = (cat) => { updateCell(r.id, "category", cat); updateCell(r.id, "sub_category", ""); };
  const setProject = (v) => { updateCell(r.id, "project_id", v); const p = projectOptions.find((x) => x.value === v); if (p) updateCell(r.id, "project_name", p.label); };
  const setPaid = (v) => { const isPaid = v === "paid"; updateCell(r.id, "paid", isPaid); updateCell(r.id, "paid_at", isPaid ? new Date().toISOString().slice(0, 10) : null); };

  return (
    <tr className={`hover:bg-slate-50 ${dirty ? "!bg-orange-50 border-l-2 border-[#F54800]" : ""} ${selected ? "!bg-orange-100" : ""}`} data-testid={`expense-row-${r.id}`}>
      {canBulk && (
        <td className={`w-9 px-2 py-2 sticky left-0 z-10 ${stickyBg}`} style={{ width: STICKY_W.checkbox }}><RowCheckbox id={r.id} /></td>
      )}
      <td className={`px-2 py-2 sticky z-10 ${stickyBg}`} style={{ left: stickyLeft, width: STICKY_W.date }}><EditableDate value={r.date} onChange={set("date")} testId={`expense-cell-date-${r.id}`} /></td>
      <td className={`px-2 py-2 sticky z-10 ${stickyBg}`} style={{ left: stickyLeft + STICKY_W.date, width: STICKY_W.account }}>
        <EditableSelect value={r.source_id} onChange={set("source_id")} options={sourceOptions} placeholder={sourceLabel} testId={`expense-cell-account-${r.id}`} />
      </td>
      <td className={`px-2 py-2 sticky z-10 ${stickyBg} shadow-[4px_0_6px_-4px_rgba(0,0,0,0.15)]`} style={{ left: stickyLeft + STICKY_W.date + STICKY_W.account, width: STICKY_W.party }}>
        <EditableSelect options={vendorOptions} value={r.vendor} onChange={set("vendor")} onAddNew={() => onAddVendor(r.id)} addNewLabel="+ Add new vendor" placeholder="Pick vendor" testId={`expense-cell-vendor-${r.id}`} />
      </td>
      <td className={`px-2 py-2 sticky z-10 ${stickyBg} shadow-[4px_0_6px_-4px_rgba(0,0,0,0.15)]`} style={{ left: stickyLeft + STICKY_W.date + STICKY_W.account + STICKY_W.party, width: STICKY_W.amount }}>
        <EditableText type="number" align="right" className="w-20" value={r.amount} onChange={set("amount")} commitOnBlur testId={`expense-cell-amount-${r.id}`} />
      </td>
      <td className="px-2 py-2 w-[90px]">
        <EditableSelect value={r.paid ? "paid" : "unpaid"} onChange={setPaid} options={PAID_OPTIONS} testId={`expense-cell-status-${r.id}`} />
        {r.paid && r.paid_at && (
          <div className="text-[9px] text-slate-400 mt-0.5" data-testid={`expense-cell-paid-at-${r.id}`}>on {String(r.paid_at).slice(0, 10)}</div>
        )}
      </td>
      <td className="px-2 py-2 min-w-[220px]"><EditableTextArea value={r.description} onChange={set("description")} testId={`expense-cell-desc-${r.id}`} /></td>
      <td className="px-2 py-2 w-32">
        <EditableSelect value={r.project_id} onChange={setProject} options={projectOptions} placeholder="—" testId={`expense-cell-project-${r.id}`} />
      </td>
      <td className="px-2 py-2 w-28"><EditableSelect value={r.department} onChange={setCascade} options={departments} testId={`expense-cell-dept-${r.id}`} /></td>
      <td className="px-2 py-2 w-32"><EditableSelect value={r.category} onChange={setCat} options={categoryOptions} onAddNew={onAddCategory} testId={`expense-cell-category-${r.id}`} /></td>
      <td className="px-2 py-2 w-32"><EditableSelect value={r.sub_category} onChange={set("sub_category")} options={subOptions} testId={`expense-cell-subcategory-${r.id}`} /></td>
      <td className="px-2 py-2 w-28"><EditableSelect value={r.payment_mode} onChange={set("payment_mode")} options={PAY_MODES} /></td>
      <td className="px-2 py-2"><EditableSelect value={r.currency || "INR"} onChange={set("currency")} options={CURRENCIES} testId={`expense-cell-currency-${r.id}`} /></td>
      <td className="px-2 py-2"><EditableText type="number" align="right" className="w-16" value={r.fx_rate} onChange={set("fx_rate")} commitOnBlur placeholder={r.currency && r.currency !== "INR" ? "e.g. 84.5" : "1"} testId={`expense-cell-fx-${r.id}`} /></td>
      <td className="px-2 py-2 text-xs text-slate-500 font-mono" data-testid={`expense-cell-inr-value-${r.id}`}>{r.currency && r.currency !== "INR" ? fmt(expenseAmountINR(r)) : "—"}</td>
      <td className="px-2 py-2"><EditableMonth value={r.month} onChange={set("month")} testId={`expense-cell-month-${r.id}`} /></td>
      <td className="px-2 py-2">
        <DriveLinkCell
          value={r.drive_link || r.invoice_link || r.txn_file_link}
          onChange={set("drive_link")}
          onPreview={(url) => onPreview({ url, title: `Receipt — ${r.description || r.vendor || r.id}` })}
          testId={`expense-cell-drive-link-${r.id}`}
        />
      </td>
      <td className="px-2 py-2 text-right">
        <button onClick={() => onRemove(r.id)} className="px-2 py-1 rounded-sm text-[10px] font-medium text-slate-700 hover:text-white hover:bg-red-600 border border-slate-200 hover:border-red-600 inline-flex items-center gap-1 transition-colors" data-testid={`expense-del-${r.id}`}>
          <Trash2 size={11} />
        </button>
      </td>
    </tr>
  );
});

export default ExpenseLedgerRow;
